import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { NoteInput } from '@/common/NoteInput';
import { NoteItem } from '@/common/NoteItem';
import { LoadMoreWrapper, TimelineWrapper } from '@/common/timelineStyles';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/student-notes',
)({
  component: MemberStudentNotesRoute,
});

export default function MemberStudentNotesRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);
  const { me } = useCurrentUser();

  // Every hook runs unconditionally before any branch: an early return above
  // them would call fewer hooks on the first render than the second, and React
  // throws as soon as the probe resolves.

  // The tab's own audience, which differs from the sidebar's
  // `getStudentViewerAccess` set (see `STUDENT_NOTE_VIEWERS`). The probe
  // answers 200 for every signed-in caller, so one query decides the page.
  const { data: access, isError: accessFailed } =
    api.getStudentNoteAccess.useQuery({
      path: { student_id: studentId },
    });

  // No explicit `limit`: the shared default of 10 is legacy's page size. Load
  // More replaces legacy's pager.
  const {
    data,
    isError: notesFailed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.listStudentNotes.useInfiniteQuery(
    { path: { student_id: studentId } },
    {
      initialPageParam: { query: { offset: 0 } },
      // `PaginatedResponse` has no `hasMore`, so it's derived from `count`, and
      // the offset is the number of rows already held.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.data.length, 0);
        return loaded < lastPage.count
          ? { query: { offset: loaded } }
          : undefined;
      },
    },
  );

  const {
    mutateAsync: createNote,
    isPending: creating,
    error: createFailed,
  } = api.createStudentNote.useMutation();
  const { mutateAsync: deleteNote, error: deleteFailed } =
    api.deleteStudentNote.useMutation();

  const [note, setNote] = useState('');
  const [added, setAdded] = useState(false);

  const handleAddNote = async (): Promise<void> => {
    setAdded(false);
    try {
      await createNote({
        path: { student_id: studentId },
        body: { content: note },
      });
      // Cleared before the refetch is awaited: `creating` goes false when the
      // POST resolves, so the Add Note button would otherwise be live over a
      // note already written.
      setNote('');
      setAdded(true);
    } catch {
      // Rendered from `failure` below.
    }
    // On both paths, so the list is the server's answer: a POST that reports
    // failure may still have written the row.
    await api.listStudentNotes.invalidateQueries();
  };

  const handleDeleteNote = async (noteId: number): Promise<void> => {
    setAdded(false);
    try {
      await deleteNote({
        path: { student_id: studentId, note_id: noteId },
      });
    } catch {
      // Rendered from `failure` below. `NoteItem` has already faded the row out
      // and doesn't fade it back, as legacy's handler also fades on failure.
    }
    // On both paths, so the list is the server's answer rather than ours.
    await api.listStudentNotes.invalidateQueries();
  };

  // ---- only now, branch ----
  const items = data?.pages.flatMap((page) => page.data) ?? [];

  // Gated positively: the screen renders only on an answered `can_view ===
  // true`, since `access` is also undefined on a 401, a 500 or a dropped
  // connection.
  //
  // Each error arm also requires that nothing is held: a failed Load More or
  // refetch sets the error on a query whose pages are still on screen, and that
  // mustn't turn into "Page not found".
  //
  // `notesFailed` counts as a refusal because the list, not the probe, takes
  // the gate; otherwise a failed list shows the empty state. `data ===
  // undefined` counts as pending so the empty state doesn't flash while the
  // list is still loading.
  const refused =
    access?.can_view === false ||
    (accessFailed && access === undefined) ||
    (notesFailed && data === undefined);
  const pending = !refused && (access === undefined || data === undefined);
  const failure = createFailed ?? deleteFailed;
  // One screen, two labels: the server says whether the subject is a student
  // or a helper/follow-up member whose notes only staff see. Legacy served the
  // second as its own `/admin-notes/` slug with the same handlers; v3 keeps one
  // route and switches the copy.
  const isAdminNotes = access?.kind === 'admin_notes';

  return (
    <MainContent>
      {refused ? (
        // Legacy denies this tab by not registering it, so a refused viewer
        // gets a 404 page, not a permission message.
        <NotFoundScreen />
      ) : pending ? (
        <LoadingSpinner centered />
      ) : (
        <>
          {added && (
            <AlertBox variant="success">
              {t(
                isAdminNotes
                  ? 'wbs.admin-note-added-successfully'
                  : 'actions.student-note-added-successfully',
              )}
            </AlertBox>
          )}
          {failure && (
            // The 422 can't happen from this UI, since `NoteInput` disables Add
            // Note on blank input, but legacy shows this string for it, so it's
            // wired. Legacy's blank-note error says "student note" for admin
            // notes too, so it is not switched on `isAdminNotes`. Matched on the error body: qraft throws the parsed body
            // and drops the `Response`, so there's no status to read.
            <AlertBox variant="error">
              {isErrorCode(failure, 'validation_error')
                ? t('lessons.please-enter-student-note')
                : t('wbs.something-went-wrong-try')}
            </AlertBox>
          )}
          {/* The composer shows for every admitted viewer, as in legacy:
             anyone who can read notes can write them. */}
          <NoteInput
            value={note}
            onChange={setNote}
            onSubmit={() => {
              void handleAddNote();
            }}
            userName={me?.display_name ?? ''}
            placeholder={t(
              isAdminNotes
                ? 'wbs.add-a-note-about-this-user'
                : 'actions.add-a-note-about',
            )}
            isSubmitting={creating}
          />
          {items.length === 0 ? (
            // Legacy shows an empty list with no message, which looks broken;
            // most students have no notes.
            <AlertBox variant="info">{t('wbs.no-results-found')}</AlertBox>
          ) : (
            <TimelineWrapper>
              {items.map((item) => (
                <NoteItem
                  key={item.id}
                  // The author's current display name, not the stored title, as
                  // legacy re-derives it.
                  name={item.author_name ?? t('actions.deleted-user')}
                  content={item.content}
                  createdAt={item.created_at}
                  authorId={item.author_id}
                  // The author's avatar, not the viewer's.
                  avatarUrl={item.avatar_url}
                  onDelete={() => {
                    void handleDeleteNote(item.id);
                  }}
                />
              ))}
              {hasNextPage && (
                <LoadMoreWrapper>
                  <WBSButton
                    wide
                    alternate
                    isLoading={isFetchingNextPage}
                    onClick={() => {
                      void fetchNextPage();
                    }}
                  >
                    {t('wbs.load-more')}
                  </WBSButton>
                </LoadMoreWrapper>
              )}
            </TimelineWrapper>
          )}
        </>
      )}
    </MainContent>
  );
}

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  margin-top: 19px;
  min-width: 0;
`;
